import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Target, X } from "lucide-react";
import { Pill } from "@/components/ui/pill";
import { derivePlanAdherenceVerdict } from "@/features/oee/plan-adherence-verdict";
import { usePlanAdherenceBounds } from "@/hooks/useOrganizationPolicy";
import { formatDisplay } from "@/lib/datetime-format";
import type { PlanAdherence, PlanAdherenceBreakdown } from "@/types/oee.types";
import { PlanAdherenceVerdictBands } from "./PlanAdherenceVerdictBands";

export interface PlanAdherenceExplanationDialogProps {
  open: boolean;
  onClose: () => void;
  planAdherence: PlanAdherence | null | undefined;
  breakdown: PlanAdherenceBreakdown | undefined;
  periodLabel: string;
}

function fmtDate(iso: string | null, estimated: boolean): string {
  const d = iso ? formatDisplay(iso) : "";
  if (!d) return "estimado";
  return estimated ? `${d} (estimado)` : d;
}

const SECTION_LABEL = "text-text-tertiary text-[11px] uppercase tracking-wider";
const ROW = "flex justify-between gap-2 px-2 py-1.5";

export function PlanAdherenceExplanationDialog({
  open,
  onClose,
  planAdherence,
  breakdown,
  periodLabel,
}: PlanAdherenceExplanationDialogProps): React.ReactElement {
  const value = planAdherence?.value ?? null;
  const basis = planAdherence?.basis ?? null;
  const unavailable = planAdherence == null || value === null || basis === null;
  const notStarted = breakdown?.fractionElapsed === 0;
  const bounds = usePlanAdherenceBounds();
  const verdict = unavailable
    ? null
    : derivePlanAdherenceVerdict(basis, value, planAdherence.overrunPct, bounds);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] animate-fade-in bg-background/80" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[61] w-full max-w-md -translate-x-1/2 -translate-y-1/2 animate-scale-in rounded-lg border bg-card p-5 shadow-lg focus:outline-none">
          <div className="mb-3 flex items-start justify-between gap-2">
            <Dialog.Title className="inline-flex items-center gap-1.5 text-base font-semibold text-text-primary">
              <Target className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
              Aderência ao plano
            </Dialog.Title>
            <Dialog.Close className="rounded p-1 text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]">
              <X className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </Dialog.Close>
          </div>

          {}
          <div className="mb-3">
            <p className={SECTION_LABEL}>Visão selecionada</p>
            <p className="mt-0.5 text-[13px] font-medium text-text-primary">{periodLabel}</p>
          </div>

          {unavailable ? (
            <Dialog.Description className="text-[13px] text-text-secondary">
              {notStarted
                ? "O plano ainda não começou — sem base para comparar. Quando a janela planejada iniciar, o percentual e o veredito aparecem aqui."
                : (breakdown?.formula ??
                  "Sem plano declarado (quantidade ou datas planejadas) para comparar. Declare o plano da produção para acompanhar a aderência.")}
            </Dialog.Description>
          ) : (
            <div className="space-y-3">
              <Dialog.Description className="text-[13px] text-text-secondary">
                {breakdown?.formula}
              </Dialog.Description>

              <div>
                <p className={SECTION_LABEL}>Janela planejada da produção</p>
                <p className="mt-0.5 text-[13px] font-medium text-text-primary">
                  {fmtDate(
                    breakdown?.plannedStartAt ?? null,
                    breakdown?.plannedStartEstimated ?? true
                  )}
                  {" → "}
                  {fmtDate(breakdown?.expectedEnd ?? null, breakdown?.expectedEndEstimated ?? true)}
                </p>
                <p className="mt-0.5 text-[11px] text-text-tertiary">
                  Janela do plano da ordem em andamento. Difere da visão selecionada acima (
                  {periodLabel.toLowerCase()}) — esta é fixa por run, aquela é o recorte temporal do
                  filtro.
                </p>
              </div>

              <div>
                <p className={cnLabel()}>Valores usados no cálculo</p>
                <dl className="bg-bg-muted-rd/40 divide-y divide-border-subtle rounded border text-[12px]">
                  <div className={ROW}>
                    <dt className="text-text-secondary">Quantidade planejada</dt>
                    <dd className="font-mono text-text-primary">{fmtNum(breakdown?.plannedQty)}</dd>
                  </div>
                  <div className={ROW}>
                    <dt className="text-text-secondary">Quantidade produzida</dt>
                    <dd className="font-mono text-text-primary">
                      {fmtNum(breakdown?.producedQty)}
                    </dd>
                  </div>
                  {breakdown?.fractionElapsed != null ? (
                    <div className={ROW}>
                      <dt className="text-text-secondary">Fração da janela decorrida</dt>
                      <dd className="font-mono text-text-primary">
                        {(breakdown.fractionElapsed * 100).toFixed(0)}%
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {basis === "in-progress" ? (
                  <p className="mt-1 text-[11px] text-text-tertiary">
                    O tempo planejado continua contando durante pausas — a fração não congela.
                  </p>
                ) : null}
              </div>

              <div className="border-t border-border-subtle pt-3">
                <p className={SECTION_LABEL}>Resultado</p>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="font-mono text-[18px] font-semibold text-text-primary">
                    {value.toFixed(1)}%
                  </span>
                  {verdict ? (
                    <Pill tone={verdict.tone} withDot>
                      {verdict.label}
                    </Pill>
                  ) : null}
                </div>
                {basis === "completed" && (planAdherence.overrunPct ?? 0) > 0 ? (
                  <p className="mt-1 text-[11px] text-rd-info-600">
                    +{planAdherence.overrunPct!.toFixed(0)}% além do plano — informado como sinal,
                    não limitado a 100%.
                  </p>
                ) : null}
              </div>

              <div>
                <p className={cnLabel()}>Veredito — faixas</p>
                <PlanAdherenceVerdictBands basis={basis} activeId={verdict?.id ?? null} />
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function cnLabel(): string {
  return `${SECTION_LABEL} mb-1.5 block`;
}

function fmtNum(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString("pt-BR");
}
